import React from 'react';
import { TrendingDown, Zap, ArrowDown, ArrowUp } from 'lucide-react';

const ProvenAtScale = () => {
  const metrics = [
    {
      icon: TrendingDown,
      value: '31%',
      label: 'RTO Reduction',
      color: 'from-green-500 to-emerald-600',
      direction: 'down'
    },
    {
      icon: () => <div className="text-2xl font-bold">₹</div>,
      value: '18%',
      label: 'Shipping Cost',
      color: 'from-blue-500 to-cyan-600',
      direction: 'down'
    },
    {
      icon: Zap,
      value: '22%',
      label: 'Faster Delivery',
      color: 'from-blue-700 to-red-600',
      direction: 'up'
    }
  ];

  return (
    <section className="relative py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 overflow-hidden">
      {/* Decorative Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(rgba(59, 130, 246, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(59, 130, 246, 0.03) 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }}></div>
        
        {/* Glowing orbs */}
        <div className="absolute top-20 left-20 w-72 h-72 bg-gradient-to-br from-blue-700/10 to-blue-800/5 rounded-full blur-3xl animate-float-slow"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-gradient-to-br from-blue-500/10 to-blue-700/5 rounded-full blur-3xl animate-float-slower"></div>
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
            Proven at <span className="bg-gradient-to-r from-blue-500 to-blue-800 bg-clip-text text-transparent">Scale</span>
          </h2>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            Real results from brands shipping with Boxd every day.
          </p>
        </div>
        
        {/* Case Study Card */}
        <div className="relative group">
          {/* Glow effect */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-700/20 to-blue-800/10 rounded-3xl opacity-0 group-hover:opacity-100 blur-2xl transition-opacity duration-300"></div>
          
          <div className="relative bg-gradient-to-br from-gray-800/90 to-gray-900/90 backdrop-blur-sm rounded-3xl border-2 border-gray-700 group-hover:border-blue-700/50 transition-all duration-300 overflow-hidden">
            {/* Header */}
            <div className="p-8 md:p-10 border-b border-gray-700">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
                    D2C Fashion Brand
                  </h3>
                  <p className="text-blue-500 font-medium">
                    12,000 orders/month · Pan-India delivery
                  </p>
                </div>
                <div className="hidden md:block w-16 h-16 bg-gradient-to-br from-blue-700 to-blue-800 rounded-2xl flex items-center justify-center">
                  <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-8 md:p-10">
              <div className="grid md:grid-cols-2 gap-8 mb-10">
                {/* Challenge */}
                <div>
                  <div className="flex items-center space-x-2 mb-4">
                    <div className="w-1 h-6 bg-red-500 rounded-full"></div>
                    <h4 className="text-xl font-bold text-white">Challenge</h4>
                  </div>
                  <p className="text-gray-300 leading-relaxed">
                    High RTO rates on COD orders and inconsistent delivery timelines across tier-2 and tier-3 cities were eroding margins.
                  </p>
                </div>
                
                {/* Solution */}
                <div>
                  <div className="flex items-center space-x-2 mb-4">
                    <div className="w-1 h-6 bg-green-500 rounded-full"></div>
                    <h4 className="text-xl font-bold text-white">Solution</h4>
                  </div>
                  <p className="text-gray-300 leading-relaxed">
                    Boxd's smart routing allocated each order to the carrier with the highest delivery success rate for that specific pincode, while automated NDR workflows resolved failed attempts within hours.
                  </p>
                </div>
              </div>
              
              {/* Results */}
              <div className="grid md:grid-cols-3 gap-6">
                {metrics.map((metric, index) => {
                  const Icon = metric.icon;
                  return (
                    <div key={index} className="relative group/metric">
                      {/* Card background */}
                      <div className="relative bg-gray-900/50 backdrop-blur-sm p-6 rounded-2xl border border-gray-700 group-hover/metric:border-blue-700/50 transition-all duration-300">
                        <div className="flex items-center justify-between mb-4">
                          <div className={`w-12 h-12 bg-gradient-to-br ${metric.color} rounded-xl flex items-center justify-center`}>
                            <Icon className="w-6 h-6 text-white" />
                          </div>
                          {metric.direction === 'down' ? (
                            <ArrowDown className="w-6 h-6 text-green-400" />
                          ) : (
                            <ArrowUp className="w-6 h-6 text-blue-500" />
                          )}
                        </div>
                        <div className={`text-4xl font-bold mb-2 bg-gradient-to-br ${metric.color} bg-clip-text text-transparent`}>
                          {metric.direction === 'down' ? '↓' : '↑'} {metric.value}
                        </div>
                        <div className="text-gray-400 font-medium">{metric.label}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Decorative corner */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-blue-700/10 to-transparent rounded-bl-full"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-blue-700/10 to-transparent rounded-tr-full"></div>
          </div>
        </div>
      </div>
      
      <style jsx>{`
        @keyframes float-slow {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          50% { transform: translateY(-30px) translateX(20px); }
        }
        
        @keyframes float-slower {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          50% { transform: translateY(30px) translateX(-20px); }
        }
        
        .animate-float-slow {
          animation: float-slow 8s ease-in-out infinite;
        }
        
        .animate-float-slower {
          animation: float-slower 10s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default ProvenAtScale;